import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, message, Tag, Row, Col, Card, Statistic, DatePicker, Divider, Descriptions, Input, Select, Popconfirm, Form } from 'antd';
import { PlusOutlined, DollarOutlined, CheckOutlined, EyeOutlined, FilterOutlined, ClearOutlined, SearchOutlined, SyncOutlined, DeleteOutlined, FilePdfOutlined, LockOutlined, OrderedListOutlined, ThunderboltOutlined, EditOutlined } from '@ant-design/icons';
import { equipeService, folhaPagamentoService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const FolhaPagamento = () => {
  const { user } = useAuth();
  const [folhas, setFolhas] = useState([]);
  const [folhasFiltradas, setFolhasFiltradas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isGerandoFolha, setIsGerandoFolha] = useState(false);
  const [mesPreview, setMesPreview] = useState(dayjs().format('YYYY-MM'));
  const [colaboradoresDisponiveis, setColaboradoresDisponiveis] = useState([]);
  const [colaboradoresSelecionados, setColaboradoresSelecionados] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [previewContexto, setPreviewContexto] = useState({
    escopo: 'GLOBAL',
    idEquipe: null,
    nomeColaborador: null,
  });
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false);
  const [detalheData, setDetalheData] = useState(null);
  const [isDetalheModalVisible, setIsDetalheModalVisible] = useState(false);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const [itensFolhaData, setItensFolhaData] = useState(null);
  const [itensFolhaAtual, setItensFolhaAtual] = useState(null);
  const [isItensModalVisible, setIsItensModalVisible] = useState(false);
  const [itensLoading, setItensLoading] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [folhaEmEdicao, setFolhaEmEdicao] = useState(null);
  const [editForm] = Form.useForm();
  
  // Estados de filtro
  const [filtros, setFiltros] = useState({
    busca_geral: '',
    mes_referencia: null,
    status_pagamento: null,
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(true);

  const compareText = (a, b) =>
    (a || '').toString().localeCompare((b || '').toString(), 'pt-BR', { sensitivity: 'base' });

  const getStatusFolha = (folha) => folha?.status || (folha?.pago ? 'PAGA' : 'RASCUNHO');

  const getStatusCor = (statusFolha) => {
    if (statusFolha === 'PAGA') return 'green';
    if (statusFolha === 'FECHADA') return 'blue';
    return 'orange';
  };

  const getComposicaoPagamento = (pagamento) => {
    const totalHoras = Number(pagamento?.total_horas || 0);
    const valorHora = Number(pagamento?.valor_hora || 0);
    const valorBaseRegular = Number(pagamento?.valor_base_regular ?? pagamento?.valor_total ?? 0);
    const valorVariavelHoras = totalHoras * valorHora;
    const valorBaseFixa = Math.max(valorBaseRegular - valorVariavelHoras, 0);

    let tipo = 'HORISTA';
    if (valorBaseFixa > 0 && valorVariavelHoras > 0) {
      tipo = 'MISTO';
    } else if (valorBaseFixa > 0 && valorVariavelHoras === 0) {
      tipo = 'MENSAL';
    }

    return {
      tipo,
      valorBaseFixa,
      valorVariavelHoras,
    };
  };

  useEffect(() => {
    fetchFolhas();
    fetchColaboradores();
  }, []);

  const aplicarFiltros = useCallback(() => {
    let resultado = [...folhas];

    // Filtro de busca geral (professor)
    if (filtros.busca_geral) {
      const busca = filtros.busca_geral.toLowerCase();
      resultado = resultado.filter((f) =>
        f.nome_professor?.toLowerCase().includes(busca)
      );
    }

    // Filtro por mês de referência
    if (filtros.mes_referencia) {
      resultado = resultado.filter(
        (f) => f.mes_referencia === filtros.mes_referencia
      );
    }

    // Filtro por status de pagamento
    if (filtros.status_pagamento !== null) {
      resultado = resultado.filter(
        (f) => f.pago === filtros.status_pagamento
      );
    }

    setFolhasFiltradas(resultado);
  }, [folhas, filtros]);

  useEffect(() => {
    aplicarFiltros();
  }, [aplicarFiltros]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  const limparFiltros = () => {
    setFiltros({
      busca_geral: '',
      mes_referencia: null,
      status_pagamento: null,
    });
  };

  const fetchFolhas = async () => {
    setLoading(true);
    try {
      const response = await folhaPagamentoService.list();
      const folhasNormalizadas = (response.data || []).map((folha) => ({
        ...folha,
        status: getStatusFolha(folha),
        valor_base_regular: Number(folha.valor_base_regular ?? folha.valor_total ?? 0),
        valor_ajustes_retroativos: Number(folha.valor_ajustes_retroativos ?? 0),
        valor_total_visual: Number(folha.valor_total_visual ?? folha.valor_total ?? 0),
      }));
      setFolhas(folhasNormalizadas);
    } catch (error) {
      message.error('Erro ao carregar pagamentos');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchColaboradores = async () => {
    try {
      const response = await equipeService.list();
      const opcoes = (response.data || [])
        .filter((equipe) => !equipe.status || equipe.status === 'ATIVO')
        .map((equipe) => ({
          value: equipe.id,
          label: equipe.nome || `Colaborador ${equipe.id}`,
        }));

      setColaboradoresDisponiveis(opcoes);
    } catch (error) {
      console.error('Erro ao carregar colaboradores para geração seletiva', error);
    }
  };

  const normalizarIdsSelecionados = (ids) => [...new Set(
    (ids || [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0)
  )];

  const handlePreview = async () => {
    try {
      const selecionadosSnapshot = [...colaboradoresSelecionados];
      const idsSelecionados = normalizarIdsSelecionados(selecionadosSnapshot);
      const mapaColaboradores = colaboradoresDisponiveis.reduce((acc, item) => {
        acc[item.value] = item.label;
        return acc;
      }, {});

      let contexto = { escopo: 'GLOBAL', idEquipe: null, nomeColaborador: null };
      let dataPreview = null;

      if (idsSelecionados.length === 1) {
        const idEquipe = idsSelecionados[0];
        const response = await folhaPagamentoService.preview(mesPreview, idEquipe);
        dataPreview = response.data;
        contexto = {
          escopo: 'INDIVIDUAL',
          idEquipe,
          nomeColaborador: mapaColaboradores[idEquipe] || `Colaborador ${idEquipe}`,
        };
      } else if (idsSelecionados.length > 1) {
        const resultados = await Promise.allSettled(
          idsSelecionados.map((idEquipe) => folhaPagamentoService.preview(mesPreview, idEquipe))
        );

        const professoresPorId = new Map();
        const erros = [];

        resultados.forEach((resultado, index) => {
          const idEquipe = idsSelecionados[index];
          const nome = mapaColaboradores[idEquipe] || `Colaborador ${idEquipe}`;

          if (resultado.status === 'fulfilled') {
            const professores = resultado.value?.data?.professores || [];
            professores.forEach((professor) => {
              if (professor?.id_equipe) {
                professoresPorId.set(professor.id_equipe, professor);
              }
            });
            return;
          }

          const detail = resultado.reason?.response?.data?.detail || resultado.reason?.message || 'Erro desconhecido';
          erros.push(`${nome}: ${detail}`);
        });

        const professores = Array.from(professoresPorId.values());
        const valorTotalGeral = professores.reduce((sum, professor) => sum + Number(professor?.valor_total || 0), 0);
        dataPreview = {
          total_professores: professores.length,
          valor_total_geral: valorTotalGeral,
          professores,
        };

        if (erros.length) {
          message.warning(`Preview parcial concluído. ${erros.length} colaborador(es) com erro.`);
          Modal.info({
            title: 'Erros no Preview Seletivo',
            width: 760,
            content: (
              <div>
                <p>Foi possível montar o preview dos demais selecionados.</p>
                <ul>
                  {erros.map((erro) => (
                    <li key={erro}>{erro}</li>
                  ))}
                </ul>
              </div>
            ),
          });
        }

        contexto = {
          escopo: 'MULTIPLO',
          idEquipe: null,
          nomeColaborador: `${idsSelecionados.length} selecionados`,
        };
      } else {
        const response = await folhaPagamentoService.preview(mesPreview);
        dataPreview = response.data;
      }

      setPreviewData(dataPreview);
      setPreviewContexto(contexto);
      setIsPreviewModalVisible(true);
    } catch (error) {
      message.error('Erro ao gerar preview');
      console.error(error);
    }
  };

  const handleGerar = () => {
    setIsGenerateModalVisible(true);
    handlePreview();
  };

  const handleConfirmGerar = async () => {
    const colaboradoresSelecionadosSnapshot = [...colaboradoresSelecionados];
    const mesReferenciaSnapshot = mesPreview;
    const houveSelecaoNoClique = colaboradoresSelecionadosSnapshot.length > 0;
    const idsSelecionados = normalizarIdsSelecionados(colaboradoresSelecionadosSnapshot);

    setIsGerandoFolha(true);
    try {
      if (houveSelecaoNoClique) {
        if (!idsSelecionados.length) {
          message.error('Seleção inválida. Revise os colaboradores selecionados antes de gerar.');
          return;
        }

        const mapaColaboradores = colaboradoresDisponiveis.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {});

        const resultados = await Promise.allSettled(
          idsSelecionados.map((idEquipe) =>
            folhaPagamentoService.gerar({
              mes_referencia: mesReferenciaSnapshot,
              id_equipe: idEquipe,
            })
          )
        );

        const sucesso = [];
        const duplicidade = [];
        const semElegibilidade = [];
        const outrosErros = [];

        resultados.forEach((resultado, index) => {
          const idEquipe = idsSelecionados[index];
          const nome = mapaColaboradores[idEquipe] || `Colaborador ${idEquipe}`;

          if (resultado.status === 'fulfilled') {
            const payload = resultado.value?.data || {};
            const folhas = payload.folhas || [];

            if (folhas.length > 0) {
              sucesso.push({ id: idEquipe, nome });
            } else {
              semElegibilidade.push({ id: idEquipe, nome });
            }
            return;
          }

          const statusCode = resultado.reason?.response?.status;
          const detail = resultado.reason?.response?.data?.detail || resultado.reason?.message || 'Erro desconhecido';
          const erroDuplicidade = statusCode === 400 && String(detail).includes('Já existe folha para este colaborador nesta competência');

          if (erroDuplicidade) {
            duplicidade.push({ id: idEquipe, nome });
          } else {
            outrosErros.push({ id: idEquipe, nome, detalhe: detail });
          }
        });

        const lista = (items) => (items.length ? items.map((item) => `${item.nome} (#${item.id})`).join(', ') : '-');

        Modal.info({
          title: 'Resumo da Geração Seletiva de Pagamento',
          width: 760,
          content: (
            <div>
              <p><strong>Competência:</strong> {mesReferenciaSnapshot}</p>
              <p><strong>Quantidade gerada com sucesso:</strong> {sucesso.length}</p>
              <p><strong>Colaboradores gerados:</strong> {lista(sucesso)}</p>
              <p><strong>Ignorados por duplicidade:</strong> {lista(duplicidade)}</p>
              <p><strong>Sem elegibilidade no mês:</strong> {lista(semElegibilidade)}</p>
              <p><strong>Outros erros:</strong></p>
              {outrosErros.length ? (
                <ul>
                  {outrosErros.map((erro) => (
                    <li key={`${erro.id}-${erro.detalhe}`}>{erro.nome} (#{erro.id}): {erro.detalhe}</li>
                  ))}
                </ul>
              ) : (
                <p>-</p>
              )}
            </div>
          ),
        });

        if (sucesso.length > 0) {
          message.success(`Geração seletiva concluída: ${sucesso.length} colaborador(es) gerado(s)`);
        } else if (duplicidade.length > 0 || semElegibilidade.length > 0) {
          message.warning('Nenhum pagamento novo foi gerado na seleção atual');
        }

        setColaboradoresSelecionados([]);
      } else {
        const response = await folhaPagamentoService.gerar({
          mes_referencia: mesReferenciaSnapshot
        });
        const folhasGeradas = response.data?.folhas || [];
        if (folhasGeradas.length === 0) {
          message.warning(
            'Nenhum pagamento foi gerado. Verifique configuração de remuneração (horas válidas e/ou salário fixo) dos colaboradores.'
          );
        } else {
          message.success(response.data.message);
        }
      }

      setIsGenerateModalVisible(false);
      setIsPreviewModalVisible(false);
      fetchFolhas();
    } catch (error) {
      message.error('Erro ao gerar pagamento');
      console.error(error);
    } finally {
      setIsGerandoFolha(false);
    }
  };

  const handleDarBaixa = async (record) => {
    Modal.confirm({
      title: 'Dar Baixa no Pagamento',
      content: (
        <div>
          <p>Deseja dar baixa no pagamento deste professor?</p>
          <p><strong>Professor:</strong> {record.nome_professor}</p>
          <p><strong>Valor:</strong> R$ {record.valor_total?.toFixed(2)}</p>
          <p><strong>Mês:</strong> {dayjs(record.mes_referencia + '-01').format('MMMM/YYYY')}</p>
        </div>
      ),
      okText: 'Sim',
      cancelText: 'Não',
      onOk: async () => {
        try {
          await folhaPagamentoService.darBaixa(record.id);
          message.success('Baixa realizada com sucesso');
          fetchFolhas();
        } catch (error) {
          message.error('Erro ao dar baixa no pagamento');
        }
      },
    });
  };

  const handleReverterBaixa = async (folhaId) => {
    try {
      await folhaPagamentoService.reverterBaixa(folhaId);
      message.success('Baixa revertida com sucesso - status voltou para FECHADA');
      fetchFolhas();
    } catch (error) {
      message.error('Erro ao reverter baixa do pagamento');
    }
  };

  const handleFecharFolha = async (record) => {
    Modal.confirm({
      title: 'Fechar Pagamento',
      content: (
        <div>
          <p>Deseja fechar este pagamento?</p>
          <p><strong>Professor:</strong> {record.nome_professor}</p>
          <p><strong>Mês:</strong> {dayjs(record.mes_referencia + '-01').format('MMMM/YYYY')}</p>
        </div>
      ),
      okText: 'Fechar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await folhaPagamentoService.fechar(record.id);
          message.success('Pagamento fechado com sucesso');
          fetchFolhas();
        } catch (error) {
          message.error('Erro ao fechar pagamento');
        }
      },
    });
  };

  const handleVerItensFolha = async (record) => {
    setItensLoading(true);
    try {
      const response = await folhaPagamentoService.itens(record.id);
      setItensFolhaData(response.data);
      setItensFolhaAtual(record);
      setIsItensModalVisible(true);
    } catch (error) {
      message.error('Erro ao carregar itens do pagamento');
    } finally {
      setItensLoading(false);
    }
  };

  const handleGerarAjustesRetroativos = async (record) => {
    try {
      const pendenciasResponse = await folhaPagamentoService.retroativosPendentes(record.id);
      const pendencias = pendenciasResponse.data || [];
      const quantidadePendencias = pendencias.length;
      const valorEstimadoTotal = pendencias.reduce((sum, item) => sum + Number(item.valor_estimado || 0), 0);
      const competenciasOrigem = Array.from(
        new Set(pendencias.map((item) => item.competencia_origem).filter(Boolean))
      ).sort();

      Modal.confirm({
        title: 'Gerar Ajustes Retroativos',
        content: (
          <div>
            <p>Gerar ajustes retroativos pendentes deste pagamento?</p>
            <p><strong>Professor:</strong> {record.nome_professor}</p>
            <p><strong>Mês destino:</strong> {dayjs(record.mes_referencia + '-01').format('MMMM/YYYY')}</p>
            <p><strong>Pendências encontradas:</strong> {quantidadePendencias}</p>
            <p><strong>Competências de origem:</strong> {competenciasOrigem.join(', ') || '-'}</p>
            <p><strong>Valor estimado total:</strong> R$ {valorEstimadoTotal.toFixed(2)}</p>
            {quantidadePendencias === 0 && (
              <p style={{ color: '#8c8c8c', marginBottom: 0 }}>
                Não há pendências retroativas de competências anteriores para este pagamento.
              </p>
            )}
          </div>
        ),
        okText: quantidadePendencias > 0 ? 'Gerar Ajustes' : 'Sem Pendências',
        cancelText: 'Cancelar',
        okButtonProps: { disabled: quantidadePendencias === 0 },
        onOk: async () => {
          try {
            const response = await folhaPagamentoService.gerarAjustesRetroativos(record.id, {});
            const data = response.data || {};
            message.success(`Ajustes processados: ${data.quantidade_itens_criados || 0} item(ns) criado(s)`);

            Modal.info({
              title: 'Resumo da Geração de Ajustes Retroativos',
              width: 680,
              content: (
                <div>
                  <p><strong>Quantidade de itens criados:</strong> {data.quantidade_itens_criados || 0}</p>
                  <p><strong>Valor total gerado:</strong> R$ {(data.valor_total_gerado || 0).toFixed(2)}</p>
                  <p><strong>Referências criadas:</strong> {(data.referencias_criadas || []).join(', ') || '-'}</p>
                  <p><strong>Referências ignoradas (já existentes):</strong> {(data.referencias_ignoradas_ja_existentes || []).join(', ') || '-'}</p>
                </div>
              ),
            });

            fetchFolhas();
          } catch (error) {
            message.error('Erro ao gerar ajustes retroativos');
          }
        },
      });
    } catch (error) {
      message.error('Erro ao carregar pendências retroativas');
    }
  };

  const handleAtualizarRascunho = async (record) => {
    Modal.confirm({
      title: 'Atualizar Pagamento Rascunho',
      content: (
        <div>
          <p>Recalcular a base regular deste pagamento conforme o tipo de remuneração (HORISTA/MENSAL/MISTO)?</p>
          <p><strong>Professor:</strong> {record.nome_professor}</p>
          <p><strong>Mês:</strong> {dayjs(record.mes_referencia + '-01').format('MMMM/YYYY')}</p>
          <p style={{ color: '#666', marginBottom: 0 }}>
            Esta ação não altera ajustes retroativos já criados.
          </p>
        </div>
      ),
      okText: 'Atualizar Rascunho',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const response = await folhaPagamentoService.atualizarRascunho(record.id);
          const folhaAtualizada = response.data || {};
          message.success(
            `Rascunho atualizado: ${Number(folhaAtualizada.total_horas || 0).toFixed(2)}h | R$ ${Number(folhaAtualizada.valor_total || 0).toFixed(2)}`
          );
          fetchFolhas();
        } catch (error) {
          message.error('Erro ao atualizar pagamento em rascunho');
        }
      },
    });
  };

  const handleDelete = async (folhaId) => {
    try {
      await folhaPagamentoService.delete(folhaId);
      message.success('Pagamento excluído com sucesso');
      fetchFolhas();
    } catch (error) {
      message.error('Erro ao excluir pagamento');
    }
  };

  const handleEditarFolha = async (record) => {
    setEditLoading(true);
    try {
      const response = await folhaPagamentoService.get(record.id);
      setFolhaEmEdicao(response.data);
      editForm.setFieldsValue({
        forma_pagamento: response.data?.forma_pagamento || '',
        observacoes: response.data?.observacoes || '',
      });
      setIsEditModalVisible(true);
    } catch (error) {
      message.error('Erro ao carregar dados do pagamento para edição');
    } finally {
      setEditLoading(false);
    }
  };

  const handleSalvarEdicaoFolha = async () => {
    if (!folhaEmEdicao?.id) return;

    try {
      const values = await editForm.validateFields();
      await folhaPagamentoService.update(folhaEmEdicao.id, {
        forma_pagamento: values.forma_pagamento || null,
        observacoes: values.observacoes || null,
      });
      message.success('Pagamento atualizado com sucesso');
      setIsEditModalVisible(false);
      setFolhaEmEdicao(null);
      fetchFolhas();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error('Erro ao atualizar pagamento');
    }
  };

  const carregarLogo = async () => {
    try {
      const response = await fetch(`${process.env.PUBLIC_URL}/logo-fisicore.png`);
      if (!response.ok) return null;
      const buffer = await response.arrayBuffer();
      const bytes = new Uint8Array(buffer);

      const isPng =
        bytes.length >= 8 &&
        bytes[0] === 0x89 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x4e &&
        bytes[3] === 0x47 &&
        bytes[4] === 0x0d &&
        bytes[5] === 0x0a &&
        bytes[6] === 0x1a &&
        bytes[7] === 0x0a;
      const isJpeg = bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;

      const mime = isJpeg ? 'image/jpeg' : isPng ? 'image/png' : null;
      if (!mime) return null;

      const blob = new Blob([buffer], { type: mime });
      const format = isJpeg ? 'JPEG' : 'PNG';

      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ dataUrl: reader.result, format });
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const handleDetalhar = async (record) => {
    setDetalheLoading(true);
    try {
      const response = await folhaPagamentoService.detalhamento(record.id);
      setDetalheData(response.data);
      setIsDetalheModalVisible(true);
    } catch (error) {
      message.error('Erro ao carregar detalhamento das aulas');
    } finally {
      setDetalheLoading(false);
    }
  };

  const handleImprimirRecibo = async (record) => {
    try {
      const [detalhamentoResponse, itensResponse] = await Promise.all([
        folhaPagamentoService.detalhamento(record.id),
        folhaPagamentoService.itens(record.id),
      ]);

      const detalhe = detalhamentoResponse.data;
      const itensFolha = itensResponse.data || {};
      const doc = new jsPDF();

      const mesFormatado = dayjs(record.mes_referencia + '-01').format('MMMM/YYYY');
      const aulas = detalhe?.aulas || [];
      const ajustesRetroativos = itensFolha?.ajustes_retroativos || [];
      const unidadesUnicas = [...new Set(aulas.map((a) => a.unidade).filter(Boolean))];
      const local = unidadesUnicas.length ? unidadesUnicas.join(' / ') : '-';
      const valorBase = Number(record.valor_base_regular ?? detalhe?.folha?.valor_total ?? record.valor_total ?? 0);
      const totalHorasBase = Number(detalhe?.folha?.total_horas ?? record.total_horas ?? 0);
      const valorHoraBase = Number(detalhe?.folha?.valor_hora ?? record.valor_hora ?? 0);
      const valorAulasBase = totalHorasBase * valorHoraBase;
      const salarioBaseFixo = Math.max(valorBase - valorAulasBase, 0);
      const totalAjustesRetroativos = ajustesRetroativos.reduce((sum, item) => sum + Number(item.valor || 0), 0);
      const valorTotalFinal = valorBase + totalAjustesRetroativos;

      const logo = await carregarLogo();
      if (logo?.dataUrl) {
        doc.addImage(logo.dataUrl, logo.format || 'PNG', 14, 10, 24, 24);
      }

      doc.setTextColor(0);

      doc.setFontSize(14);
      doc.text('RECIBO DE PAGAMENTO', 105, 16, { align: 'center' });

      doc.setFontSize(10);
      const headerY = 25;
      doc.text('EMPRESA: Fisicore Assessoria de Dança e Esporte', 14, headerY);
      doc.text('CNPJ: 37.642.965/0001-98', 14, headerY + 6);
      doc.text(`PRESTADOR DE SERVIÇOS: ${record.nome_professor || '-'}`, 14, headerY + 12);
      doc.text('SERVIÇO PRESTADO: Prestação de serviços (aulas ministradas) + ajustes retroativos', 14, headerY + 18);

      doc.text(`REFERÊNCIA: ${mesFormatado}`, 14, headerY + 28);
      doc.text(`LOCAL: ${local}`, 14, headerY + 34);
      doc.text(`VALOR HORA / AULA: R$ ${record.valor_hora?.toFixed(2) || '0.00'}`, 14, headerY + 40);
      doc.text(`TOTAL HORAS (base regular): ${Number(detalhe?.folha?.total_horas || 0).toFixed(2)}`, 14, headerY + 46);

      autoTable(doc, {
        startY: headerY + 52,
        head: [["Data", "Unidade", "Modalidade", "Turma", "Início", "Fim", "Horas"]],
        body: aulas.map((a) => [
          a.data_aula ? dayjs(a.data_aula).format('DD/MM/YYYY') : '-',
          a.unidade || '-',
          a.modalidade || '-',
          a.turma || '-',
          a.hora_inicio || '-',
          a.hora_fim || '-',
          (a.duracao_horas || 0).toFixed(2),
        ]),
        theme: 'plain',
        styles: {
          fontSize: 8,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          fillColor: [255, 255, 255],
        },
        headStyles: {
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          fillColor: [255, 255, 255],
        },
        bodyStyles: {
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          fillColor: [255, 255, 255],
        },
      });

      let currentY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 8 : headerY + 126;

      doc.setFontSize(10);
      doc.text('AJUSTES RETROATIVOS (competências anteriores)', 14, currentY);
      currentY += 4;

      if (ajustesRetroativos.length > 0) {
        autoTable(doc, {
          startY: currentY,
          head: [["Descrição", "Comp. Origem", "Data Aula", "Valor"]],
          body: ajustesRetroativos.map((item) => [
            item.descricao_exibicao || item.descricao || '-',
            item.competencia_origem || '-',
            item.data_aula_referencia ? dayjs(item.data_aula_referencia).format('DD/MM/YYYY') : '-',
            `R$ ${Number(item.valor || 0).toFixed(2)}`,
          ]),
          theme: 'plain',
          styles: {
            fontSize: 8,
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            fillColor: [255, 255, 255],
          },
          headStyles: {
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            fillColor: [255, 255, 255],
          },
          bodyStyles: {
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            fillColor: [255, 255, 255],
          },
        });
        currentY = (doc.lastAutoTable?.finalY || currentY) + 8;
      } else {
        doc.text('Sem ajustes retroativos.', 14, currentY + 4);
        currentY += 12;
      }

      doc.setFontSize(10);
      doc.text('COMPOSIÇÃO DO PAGAMENTO:', 14, currentY);
      currentY += 6;

      if (salarioBaseFixo > 0) {
        doc.text(`Salário base (fixo mensal): R$ ${salarioBaseFixo.toFixed(2)}`, 14, currentY);
        currentY += 6;
      }

      if (valorAulasBase > 0) {
        doc.text(`Valor por aulas (horas validadas): R$ ${valorAulasBase.toFixed(2)}`, 14, currentY);
        currentY += 6;
      }

      doc.text(`Subtotal (serviços do período): R$ ${valorBase.toFixed(2)}`, 14, currentY);
      doc.text(`Total ajustes retroativos: R$ ${totalAjustesRetroativos.toFixed(2)}`, 14, currentY + 6);
      doc.text(`Total final pago/previsto: R$ ${valorTotalFinal.toFixed(2)}`, 14, currentY + 12);

      doc.text('Declaro que recebi a importância discriminada acima neste recibo:', 14, currentY + 24);
      doc.text(`Data: ${record.data_pagamento ? dayjs(record.data_pagamento).format('DD/MM/YYYY') : '-'}`, 14, currentY + 32);
      doc.text('Assinatura: __________________________________________', 14, currentY + 40);

      doc.save(`recibo_${record.nome_professor || 'professor'}_${record.mes_referencia}.pdf`);
    } catch (error) {
      message.error('Erro ao gerar recibo PDF');
    }
  };

  const columns = [
    {
      title: 'Mês/Ano',
      dataIndex: 'mes_referencia',
      key: 'mes_referencia',
      render: (mes) => dayjs(mes + '-01').format('MMMM/YYYY'),
      width: 120,
      sorter: (a, b) => compareText(a.mes_referencia, b.mes_referencia),
    },
    {
      title: 'Professor',
      dataIndex: 'nome_professor',
      key: 'nome_professor',
      sorter: (a, b) => compareText(a.nome_professor, b.nome_professor),
    },
    {
      title: 'Total Horas',
      dataIndex: 'total_horas',
      key: 'total_horas',
      width: 100,
      render: (horas) => horas?.toFixed(2),
    },
    {
      title: 'Valor/Hora',
      dataIndex: 'valor_hora',
      key: 'valor_hora',
      width: 100,
      render: (valor) => `R$ ${valor?.toFixed(2)}`,
    },
    {
      title: 'Valor Total',
      dataIndex: 'valor_total',
      key: 'valor_total',
      width: 220,
      render: (_, record) => {
        const base = Number(record.valor_base_regular ?? record.valor_total ?? 0);
        const ajustes = Number(record.valor_ajustes_retroativos ?? 0);
        const totalVisual = Number(record.valor_total_visual ?? (base + ajustes));
        const composicao = getComposicaoPagamento(record);
        return (
          <div>
            <Tag color={composicao.tipo === 'MISTO' ? 'cyan' : composicao.tipo === 'MENSAL' ? 'geekblue' : 'gold'}>
              {composicao.tipo}
            </Tag>
            <div style={{ fontSize: '12px', color: '#595959' }}>Base fixa: R$ {composicao.valorBaseFixa.toFixed(2)}</div>
            <div style={{ fontSize: '12px', color: '#595959' }}>Componente horas: R$ {composicao.valorVariavelHoras.toFixed(2)}</div>
            <div style={{ fontSize: '12px', color: '#595959' }}>Base: R$ {base.toFixed(2)}</div>
            <div style={{ fontSize: '12px', color: '#d46b08' }}>Ajustes: R$ {ajustes.toFixed(2)}</div>
            <strong style={{ color: '#1890ff' }}>Final: R$ {totalVisual.toFixed(2)}</strong>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const statusFolha = getStatusFolha(record);
        const pago = statusFolha === 'PAGA';

        return (
        <div>
          <Tag color={getStatusCor(statusFolha)}>
            {statusFolha}
          </Tag>
          {pago && record.data_pagamento && (
            <div style={{ fontSize: '11px', color: '#999', marginTop: 4 }}>
              {dayjs(record.data_pagamento).format('DD/MM/YYYY')}
            </div>
          )}
        </div>
        );
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 280,
      render: (_, record) => {
        const statusFolha = getStatusFolha(record);
        const isRascunho = statusFolha === 'RASCUNHO';
        const isFechada = statusFolha === 'FECHADA';
        const isPaga = statusFolha === 'PAGA';

        return (
        <Space wrap>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleDetalhar(record)}
            size="small"
            title="Detalhar Aulas"
          />
          <Button
            icon={<OrderedListOutlined />}
            onClick={() => handleVerItensFolha(record)}
            size="small"
            title="Itens do Pagamento"
          />
          <Button
            icon={<FilePdfOutlined />}
            onClick={() => handleImprimirRecibo(record)}
            size="small"
            title="Recibo PDF"
          />
          {isRascunho && (
            <Button
              icon={<SyncOutlined />}
              onClick={() => handleAtualizarRascunho(record)}
              size="small"
              title="Atualizar Rascunho"
            >
              Atualizar
            </Button>
          )}
          {isRascunho && (
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditarFolha(record)}
              size="small"
              title="Editar Pagamento"
            >
              Editar
            </Button>
          )}
          {isRascunho && (
            <Button
              icon={<LockOutlined />}
              onClick={() => handleFecharFolha(record)}
              size="small"
              title="Fechar Pagamento"
            >
              Fechar
            </Button>
          )}
          {(isRascunho || isFechada) && (
            <Button
              icon={<CheckOutlined />}
              onClick={() => handleDarBaixa(record)}
              size="small"
              type="primary"
              title="Dar Baixa"
            />
          )}
          {isRascunho && (
            <Button
              icon={<ThunderboltOutlined />}
              onClick={() => handleGerarAjustesRetroativos(record)}
              size="small"
              title="Gerar Ajustes Retroativos"
            >
              Gerar Ajustes
            </Button>
          )}
          {isPaga && user?.role === 'ADMIN' && (
            <Popconfirm
              title="Tem certeza que deseja reverter esta baixa? O status voltará para FECHADA."
              onConfirm={() => handleReverterBaixa(record.id)}
              okText="Sim"
              cancelText="Não"
            >
              <Button
                icon={<SyncOutlined />}
                size="small"
                type="default"
                title="Reverter"
              >
                Reverter
              </Button>
            </Popconfirm>
          )}
          {user?.role === 'ADMIN' && isRascunho && (
            <Popconfirm
              title="Tem certeza que deseja excluir este pagamento?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sim"
              cancelText="Não"
            >
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                title="Excluir"
              />
            </Popconfirm>
          )}
        </Space>
        );
      },
    },
  ];

  const itensConsolidados = [
    ...(itensFolhaData?.itens_regulares || []),
    ...(itensFolhaData?.ajustes_retroativos || []),
  ]
    .filter((item, index, arr) => arr.findIndex((other) => other.id === item.id) === index)
    .sort((a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf());

  const totalAjustesRetroativosItens = (itensFolhaData?.ajustes_retroativos || [])
    .reduce((sum, item) => sum + Number(item.valor || 0), 0);

  // Calcular estatísticas (baseado nos dados filtrados)
  const totalPago = folhasFiltradas
    .filter(f => f.pago)
    .reduce((sum, f) => sum + Number(f.valor_total_visual ?? f.valor_total ?? 0), 0);
  
  const totalPendente = folhasFiltradas
    .filter(f => !f.pago)
    .reduce((sum, f) => sum + Number(f.valor_total_visual ?? f.valor_total ?? 0), 0);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Pago"
              value={totalPago}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Pendente"
              value={totalPendente}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Geral"
              value={totalPago + totalPendente}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card 
        title="Gerar Pagamento" 
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <DatePicker
              picker="month"
              format="MM/YYYY"
              value={dayjs(mesPreview)}
              onChange={(date) => setMesPreview(date.format('YYYY-MM'))}
              placeholder="Selecione o mês"
            />
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Selecionar colaborador(es)"
              value={colaboradoresSelecionados}
              onChange={(values) => setColaboradoresSelecionados(values)}
              options={colaboradoresDisponiveis}
              style={{ minWidth: 280 }}
              maxTagCount="responsive"
            />
            <Button
              onClick={() => setColaboradoresSelecionados([])}
              disabled={!colaboradoresSelecionados.length}
            >
              Limpar seleção
            </Button>
            <Button 
              icon={<EyeOutlined />} 
              onClick={handlePreview}
            >
              Preview
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleGerar}
              loading={isGerandoFolha}
            >
              Gerar Pagamento
            </Button>
          </Space>
        }
      >
        <p>
          Selecione o mês de referência e clique em "Preview" para visualizar os cálculos 
          ou "Gerar Pagamento" para criar os registros conforme o tipo de remuneração de cada colaborador.
        </p>
        <p style={{ marginTop: 8, marginBottom: 0, color: '#666' }}>
          Regra: HORISTA = aulas validadas × valor hora | MENSAL = salário fixo | MISTO = salário fixo + aulas validadas × valor hora.
        </p>
        <p style={{ marginBottom: 0, color: '#666' }}>
          Se nenhum colaborador for selecionado, a geração será para todos os elegíveis.
        </p>
        <p style={{ marginTop: 8, marginBottom: 0, color: '#666' }}>
          Preview: {colaboradoresSelecionados.length === 0
            ? 'global (todos os elegíveis)'
            : colaboradoresSelecionados.length === 1
              ? `individual para ${colaboradoresDisponiveis.find((item) => item.value === colaboradoresSelecionados[0])?.label || `Colaborador ${colaboradoresSelecionados[0]}`}`
              : `seletivo para ${colaboradoresSelecionados.length} colaboradores`
          }
        </p>
        {colaboradoresSelecionados.length > 0 && (
          <p style={{ marginTop: 8, marginBottom: 0, color: '#595959' }}>
            Selecionados ({colaboradoresSelecionados.length}): {
              colaboradoresSelecionados
                .map((id) => colaboradoresDisponiveis.find((item) => item.value === id)?.label || `Colaborador ${id}`)
                .join(', ')
            }
          </p>
        )}
      </Card>

      {/* Painel de Filtros */}
      <Card 
        title={
          <Space>
            <FilterOutlined />
            Filtros de Busca
            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
              ({folhasFiltradas.length} de {folhas.length} pagamentos)
            </span>
          </Space>
        }
        extra={
          <Button type="text" size="small" onClick={() => setMostrarFiltros(!mostrarFiltros)}>
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {mostrarFiltros && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Busca Geral</div>
              <Input
                placeholder="Pesquisar por professor..."
                prefix={<SearchOutlined />}
                value={filtros.busca_geral}
                onChange={(e) => handleFiltroChange('busca_geral', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Mês de Referência</div>
              <DatePicker
                placeholder="Selecione o mês"
                picker="month"
                value={filtros.mes_referencia ? dayjs(filtros.mes_referencia) : null}
                onChange={(date) => handleFiltroChange('mes_referencia', date ? date.format('YYYY-MM') : null)}
                style={{ width: '100%' }}
                format="MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Status Pagamento</div>
              <Select
                placeholder="Todos os status"
                value={filtros.status_pagamento}
                onChange={(value) => handleFiltroChange('status_pagamento', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value={true}>Pago</Select.Option>
                <Select.Option value={false}>Pendente</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>&nbsp;</div>
              <Button icon={<ClearOutlined />} onClick={limparFiltros} block>
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      <Table
        columns={columns}
        dataSource={folhasFiltradas}
        loading={loading}
        rowKey="id"
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total de ${total} pagamentos`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        scroll={{ x: 1000 }}
      />

      {/* Modal Preview */}
      <Modal
        title={`Preview - ${dayjs(mesPreview + '-01').format('MMMM/YYYY')}`}
        open={isPreviewModalVisible}
        onCancel={() => setIsPreviewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsPreviewModalVisible(false)}>
            Fechar
          </Button>
        ]}
        width={900}
      >
        {previewData && (
          <div>
            <p style={{ marginBottom: 12, color: previewContexto.escopo !== 'GLOBAL' ? '#1d39c4' : '#666' }}>
              <strong>Escopo do preview:</strong> {previewContexto.escopo === 'INDIVIDUAL'
                ? `Individual (${previewContexto.nomeColaborador})`
                : previewContexto.escopo === 'MULTIPLO'
                  ? `Seletivo (${previewContexto.nomeColaborador})`
                  : 'Global (todos os elegíveis)'}
            </p>
            <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Total de Professores">
                {previewData.total_professores}
              </Descriptions.Item>
              <Descriptions.Item label="Valor Total Geral">
                R$ {previewData.valor_total_geral?.toFixed(2)}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Detalhamento por Professor</Divider>

            <Table
              dataSource={previewData.professores}
              rowKey="id_equipe"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Professor',
                  dataIndex: 'nome_professor',
                  key: 'nome_professor',
                },
                {
                  title: 'Tipo',
                  dataIndex: 'tipo_remuneracao',
                  key: 'tipo_remuneracao',
                  width: 110,
                  render: (tipo) => tipo || '-',
                },
                {
                  title: 'Qtd Aulas',
                  dataIndex: 'quantidade_aulas',
                  key: 'quantidade_aulas',
                  width: 100,
                },
                {
                  title: 'Base Fixa',
                  dataIndex: 'valor_base_fixa',
                  key: 'valor_base_fixa',
                  width: 120,
                  render: (valor) => `R$ ${Number(valor || 0).toFixed(2)}`,
                },
                {
                  title: 'Total Horas',
                  dataIndex: 'total_horas',
                  key: 'total_horas',
                  width: 100,
                  render: (horas) => horas?.toFixed(2),
                },
                {
                  title: 'Valor/Hora',
                  dataIndex: 'valor_hora',
                  key: 'valor_hora',
                  width: 100,
                  render: (valor) => `R$ ${valor?.toFixed(2)}`,
                },
                {
                  title: 'Valor Total',
                  dataIndex: 'valor_total',
                  key: 'valor_total',
                  width: 120,
                  render: (valor) => (
                    <strong>R$ {valor?.toFixed(2)}</strong>
                  ),
                },
                {
                  title: 'Status',
                  dataIndex: 'ja_gerado',
                  key: 'ja_gerado',
                  width: 100,
                  render: (gerado) => (
                    <Tag color={gerado ? 'blue' : 'default'}>
                      {gerado ? 'Já Gerado' : 'Novo'}
                    </Tag>
                  ),
                },
              ]}
            />
          </div>
        )}
      </Modal>

      {/* Modal Gerar Pagamento */}
      <Modal
        title={`Gerar Pagamento - ${dayjs(mesPreview + '-01').format('MMMM/YYYY')}`}
        open={isGenerateModalVisible}
        onOk={handleConfirmGerar}
        onCancel={() => setIsGenerateModalVisible(false)}
        okText="Confirmar e Gerar"
        cancelText="Cancelar"
        confirmLoading={isGerandoFolha}
        width={900}
      >
        {previewData && (
          <div>
            {colaboradoresSelecionados.length > 0 ? (
              <>
                <p style={{ marginBottom: 8 }}>
                  Geração seletiva para <strong>{colaboradoresSelecionados.length}</strong> colaborador(es) selecionado(s).
                </p>
                <p style={{ marginBottom: 16 }}>
                  <strong>Selecionados:</strong> {
                    colaboradoresSelecionados
                      .map((id) => colaboradoresDisponiveis.find((item) => item.value === id)?.label || `Colaborador ${id}`)
                      .join(', ')
                  }
                </p>
                {colaboradoresSelecionados.length > 1 && (
                  <p style={{ marginBottom: 16, color: '#d46b08' }}>
                    <strong>Atenção:</strong> será feita uma geração por colaborador para todos os selecionados acima.
                  </p>
                )}
              </>
            ) : (
              <p style={{ marginBottom: 16, color: '#666' }}>
                Nenhum colaborador selecionado: a geração será para todos os elegíveis.
              </p>
            )}

            <p style={{ marginBottom: 16 }}>
              Serão gerados <strong>{previewData.professores?.filter(p => !p.ja_gerado).length}</strong> registro(s) 
              de pagamento com valor total de <strong>R$ {
                previewData.professores
                  ?.filter(p => !p.ja_gerado)
                  .reduce((sum, p) => sum + p.valor_total, 0)
                  .toFixed(2)
              }</strong>.
            </p>

            <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Total de Professores">
                {previewData.total_professores}
              </Descriptions.Item>
              <Descriptions.Item label="Valor Total Geral">
                R$ {previewData.valor_total_geral?.toFixed(2)}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Detalhamento por Professor</Divider>

            <Table
              dataSource={previewData.professores}
              rowKey="id_equipe"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Professor',
                  dataIndex: 'nome_professor',
                  key: 'nome_professor',
                },
                {
                  title: 'Total Horas',
                  dataIndex: 'total_horas',
                  key: 'total_horas',
                  width: 100,
                  render: (horas) => horas?.toFixed(2),
                },
                {
                  title: 'Valor Total',
                  dataIndex: 'valor_total',
                  key: 'valor_total',
                  width: 120,
                  render: (valor) => (
                    <strong>R$ {valor?.toFixed(2)}</strong>
                  ),
                },
                {
                  title: 'Status',
                  dataIndex: 'ja_gerado',
                  key: 'ja_gerado',
                  width: 100,
                  render: (gerado) => (
                    <Tag color={gerado ? 'blue' : 'green'}>
                      {gerado ? 'Já Gerado' : 'Será Gerado'}
                    </Tag>
                  ),
                },
              ]}
            />
          </div>
        )}
      </Modal>

      {/* Modal Detalhamento */}
      <Modal
        title="Detalhamento das Aulas"
        open={isDetalheModalVisible}
        onCancel={() => setIsDetalheModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsDetalheModalVisible(false)}>
            Fechar
          </Button>
        ]}
        width={900}
      >
        {detalheData && (
          <div>
            <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Professor">
                {detalheData.folha?.nome_professor || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Mês">
                {dayjs(detalheData.folha?.mes_referencia + '-01').format('MMMM/YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Total Horas">
                {detalheData.folha?.total_horas?.toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Valor Hora">
                R$ {detalheData.folha?.valor_hora?.toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Valor Total">
                R$ {detalheData.folha?.valor_total?.toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Origem do Detalhamento">
                {detalheData.folha?.origem_detalhamento === 'SNAPSHOT_FECHAMENTO_GERACAO'
                  ? 'Snapshot no fechamento/geração'
                  : 'Aulas regulares da competência'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>
              {detalheData.folha?.origem_detalhamento === 'SNAPSHOT_FECHAMENTO_GERACAO'
                ? 'Aulas que compuseram o pagamento no fechamento/geração'
                : 'Aulas regulares da competência'}
            </Divider>

            <Table
              dataSource={detalheData.aulas}
              rowKey={(_, index) => index}
              pagination={false}
              size="small"
              loading={detalheLoading}
              columns={[
                {
                  title: 'Data',
                  dataIndex: 'data_aula',
                  key: 'data_aula',
                  width: 100,
                  render: (data) => dayjs(data).format('DD/MM/YYYY'),
                },
                {
                  title: 'Unidade',
                  dataIndex: 'unidade',
                  key: 'unidade',
                },
                {
                  title: 'Modalidade',
                  dataIndex: 'modalidade',
                  key: 'modalidade',
                },
                {
                  title: 'Turma',
                  dataIndex: 'turma',
                  key: 'turma',
                },
                {
                  title: 'Início',
                  dataIndex: 'hora_inicio',
                  key: 'hora_inicio',
                  width: 80,
                },
                {
                  title: 'Fim',
                  dataIndex: 'hora_fim',
                  key: 'hora_fim',
                  width: 80,
                },
                {
                  title: 'Horas',
                  dataIndex: 'duracao_horas',
                  key: 'duracao_horas',
                  width: 80,
                  render: (h) => h?.toFixed(2),
                },
              ]}
            />
          </div>
        )}
      </Modal>

      <Modal
        title="Editar Pagamento"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setFolhaEmEdicao(null);
        }}
        onOk={handleSalvarEdicaoFolha}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={editLoading}
        width={640}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="Forma de Pagamento"
            name="forma_pagamento"
            rules={[{ max: 50, message: 'Máximo de 50 caracteres' }]}
          >
            <Input placeholder="Ex: PIX, Transferência, Dinheiro" maxLength={50} />
          </Form.Item>

          <Form.Item label="Observações" name="observacoes">
            <Input.TextArea
              rows={4}
              placeholder="Observações complementares do pagamento"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Itens do Pagamento */}
      <Modal
        title={`Itens do Pagamento - ${itensFolhaAtual?.nome_professor || ''} ${itensFolhaAtual?.mes_referencia ? `(${dayjs(itensFolhaAtual.mes_referencia + '-01').format('MM/YYYY')})` : ''}`}
        open={isItensModalVisible}
        onCancel={() => setIsItensModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsItensModalVisible(false)}>
            Fechar
          </Button>
        ]}
        width={1080}
      >
        <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Itens Regulares">
            {(itensFolhaData?.itens_regulares || []).length}
          </Descriptions.Item>
          <Descriptions.Item label="Ajustes Retroativos">
            {(itensFolhaData?.ajustes_retroativos || []).length}
          </Descriptions.Item>
          <Descriptions.Item label="Total de Itens">
            {itensConsolidados.length}
          </Descriptions.Item>
          <Descriptions.Item label="Total Ajustes Retroativos">
            R$ {totalAjustesRetroativosItens.toFixed(2)}
          </Descriptions.Item>
        </Descriptions>

        <Table
          dataSource={itensConsolidados}
          rowKey={(item) => `${item.id}`}
          pagination={{ pageSize: 8 }}
          size="small"
          loading={itensLoading}
          columns={[
            {
              title: 'Tipo',
              dataIndex: 'tipo_item',
              key: 'tipo_item',
              width: 150,
              render: (tipo) => <Tag color={tipo === 'AJUSTE_RETROATIVO' ? 'volcano' : 'default'}>{tipo}</Tag>,
            },
            {
              title: 'Descrição',
              dataIndex: 'descricao_exibicao',
              key: 'descricao',
              render: (v, item) => v || item.descricao,
            },
            {
              title: 'Valor',
              dataIndex: 'valor',
              key: 'valor',
              width: 120,
              render: (valor) => `R$ ${Number(valor || 0).toFixed(2)}`,
            },
            {
              title: 'Competência Origem',
              dataIndex: 'competencia_origem',
              key: 'competencia_origem',
              width: 150,
              render: (v) => v || '-',
            },
            {
              title: 'Referência Tipo',
              dataIndex: 'referencia_tipo',
              key: 'referencia_tipo',
              width: 150,
              render: (v) => v || '-',
            },
            {
              title: 'Referência ID',
              dataIndex: 'referencia_id',
              key: 'referencia_id',
              width: 120,
              render: (v) => v || '-',
            },
            {
              title: 'Data Aula Ref.',
              dataIndex: 'data_aula_referencia',
              key: 'data_aula_referencia',
              width: 130,
              render: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '-'),
            },
            {
              title: 'Horas Ref.',
              dataIndex: 'horas_referencia',
              key: 'horas_referencia',
              width: 110,
              render: (v) => (v !== null && v !== undefined ? Number(v).toFixed(2) : '-'),
            },
            {
              title: 'Criado em',
              dataIndex: 'created_at',
              key: 'created_at',
              width: 160,
              render: (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '-'),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default FolhaPagamento;
